import { signal } from '@preact/signals-core';
import { accessTokenSignal, type UserRole } from '../store/auth';
import { escapeHtml } from '../utils/sanitize';
import { showToast } from '../utils/toast';

export function getCustomerName(customerId?: string | null): string {
	if (!customerId) return 'Unknown Customer';
	const all = allCustomersSignal.value || [];
	const foundAll = all.find((c: any) => c.id === customerId);
	if (foundAll) return foundAll.name;
	const exp = explorerCustomersSignal.value || [];
	const foundExp = exp.find((c: any) => c.id === customerId);
	if (foundExp) return foundExp.name;
	const cust = customersSignal.value || [];
	const foundCust = cust.find((c: any) => c.id === customerId);
	if (foundCust) return foundCust.name;
	return 'Customer';
}

// Signals for state management
export const assetsListSignal = signal<any[]>([]);
export const customersSignal = signal<any[]>([]);
export const allCustomersSignal = signal<any[]>([]);
export const customersPageMetaSignal = signal({ page: 1, pageSize: 20, total: 0 });
export const explorerCustomersSignal = signal<any[]>([]);
export const explorerCustomersMetaSignal = signal({ page: 1, pageSize: 12, total: 0 });
export const sitesSignal = signal<any[]>([]);
export const deviceModelsSignal = signal<any[]>([]);
export const pageTypesSignal = signal<any[]>([]);

// Filter and Role-switch signals
export const activeRoleSignal = signal<UserRole>('admin');
export const activeCustomerSignal = signal<string>('00000000-0000-0000-0000-000000000001');
export const activeFiltersSignal = signal({
	customerId: 'all',
	siteId: 'all',
	status: 'all',
});

// Dynamic modal states
export const selectedAssetIdSignal = signal<string | null>(null);
export const selectedReadingSignal = signal<any | null>(null);
export const selectedReadingIdSignal = signal<string | null>(null);
export const registrationSelectedCustomerIdSignal = signal<string>('00000000-0000-0000-0000-000000000001');

// JWT generation helper for mock Bearer auth
export function generateToken(role: UserRole, customerId: string, sites: string[]): string {
	const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
	const payload = btoa(
		JSON.stringify({
			sub: 'user_dev',
			role: role,
			customer_id: customerId,
			sites: sites,
			exp: Math.floor(Date.now() / 1000) + (365 * 24 * 3600), // 1 year expiry for dev simulation
		})
	);
	return `${header}.${payload}.sig`;
}

export function getAuthHeaders() {
	if (accessTokenSignal.value) {
		return {
			'Authorization': `Bearer ${accessTokenSignal.value}`,
			'Content-Type': 'application/json',
		};
	}

	const role = activeRoleSignal.value;
	const customerId = activeCustomerSignal.value;
	let sites: string[] = [];

	// Assign sites based on selected customer context
	if (customerId === '00000000-0000-0000-0000-000000000001') {
		sites = ['00000000-0000-0000-0000-000000000011', '00000000-0000-0000-0000-000000000012'];
	} else if (customerId === '00000000-0000-0000-0000-000000000002') {
		sites = ['00000000-0000-0000-0000-000000000021'];
	}

	const token = generateToken(role, customerId, sites);
	return {
		'Authorization': `Bearer ${token}`,
		'Content-Type': 'application/json',
	};
}

// Fetch helper functions
export async function refreshAssets() {
	const filters = activeFiltersSignal.value;
	let url = '/api/v1/assets?';
	if (filters.customerId && filters.customerId !== 'all') {
		url += `customerId=${filters.customerId}&`;
	}
	if (filters.siteId && filters.siteId !== 'all') {
		url += `siteId=${filters.siteId}&`;
	}
	if (filters.status && filters.status !== 'all') {
		url += `status=${filters.status}&`;
	}

	try {
		const res = await fetch(url, { headers: getAuthHeaders() });
		if (res.ok) {
			assetsListSignal.value = await res.json();
		} else {
			assetsListSignal.value = [];
		}
	} catch (e) {
		console.error('failed to fetch assets', e);
	}
}

type CustomerIndexResponse = {
	items: Array<{ id: string; name: string }>;
	page: number;
	pageSize: number;
	total: number;
};

async function fetchCustomerIndex(page: number, pageSize: number, search: string, assetsOnly: boolean): Promise<CustomerIndexResponse | null> {
	const params = new URLSearchParams({
		page: String(page),
		pageSize: String(pageSize),
	});
	if (search.trim()) {
		params.set('search', search.trim());
	}
	if (assetsOnly) {
		params.set('assetsOnly', 'true');
	}

	try {
		const res = await fetch(`/api/v1/customers/index?${params.toString()}`, { headers: getAuthHeaders() });
		if (!res.ok) {
			return null;
		}
		return await res.json() as CustomerIndexResponse;
	} catch (err) {
		console.error('Failed to fetch customer index', err);
		return null;
	}
}

export async function refreshCustomerPage(page = 1, pageSize = 20, search = '') {
	const result = await fetchCustomerIndex(page, pageSize, search, false);
	if (!result) {
		customersSignal.value = [];
		customersPageMetaSignal.value = { page, pageSize, total: 0 };
		return;
	}

	customersSignal.value = result.items;
	customersPageMetaSignal.value = { page: result.page, pageSize: result.pageSize, total: result.total };
}

export async function refreshExplorerCustomerPage(page = 1, pageSize = 12, search = '') {
	const result = await fetchCustomerIndex(page, pageSize, search, true);
	if (!result) {
		explorerCustomersSignal.value = [];
		explorerCustomersMetaSignal.value = { page, pageSize, total: 0 };
		return;
	}

	explorerCustomersSignal.value = result.items;
	explorerCustomersMetaSignal.value = { page: result.page, pageSize: result.pageSize, total: result.total };
}

export async function searchCustomersForAutocomplete(query: string): Promise<Array<{ id: string; label: string }>> {
	const result = await fetchCustomerIndex(1, 20, query, false);
	return (result?.items || []).map((item) => ({ id: item.id, label: item.name }));
}

export async function searchModelsForAutocomplete(query: string): Promise<Array<{ id: string; label: string }>> {
	const models = deviceModelsSignal.value || [];
	const q = (query || '').toLowerCase().trim();
	const filtered = !q
		? models
		: models.filter((m: any) =>
				(`${m.vendor || ''} ${m.modelName || ''}`).toLowerCase().includes(q)
		  );
	return filtered.map((m: any) => ({
		id: m.id,
		label: `${m.vendor} ${m.modelName}`.trim(),
	}));
}

export const explorerLevelSignal = signal<'customers' | 'departments' | 'assets'>('customers');
export const selectedExplorerCustomerIdSignal = signal<string | null>(null);
export const selectedExplorerDepartmentIdSignal = signal<string | null>(null);
export const assetsMpsFilterSignal = signal<'all' | 'mps' | 'non-mps'>(
	(typeof localStorage !== 'undefined' && localStorage.getItem('opm_assets_mps_filter') as any) || 'all'
);
export const departmentsForExplorerSignal = signal<any[]>([]);

export async function refreshExplorerDepartments(customerId: string) {
	try {
		const res = await fetch(`/api/v1/departments?customerId=${customerId}`, {
			headers: getAuthHeaders(),
		});
		if (res.ok) {
			departmentsForExplorerSignal.value = await res.json();
		} else {
			departmentsForExplorerSignal.value = [];
		}
	} catch (e) {
		console.error('Failed to fetch departments for explorer', e);
	}
}

export async function refreshAssetReadings(assetId: string) {
	const table = document.getElementById('readings-log-table') as any;
	if (!table) return;

	const role = activeRoleSignal.value;
	const isManagerOrAdmin = role === 'superadmin' || role === 'admin' || role === 'manager';

	const pageTypes = pageTypesSignal.value || [];
	const ptMap = new Map(pageTypes.map(pt => [pt.id, pt.displayName]));

	table.columns = [
		{
			key: 'createdAt',
			header: 'Date & Time',
			sortable: true,
			sortType: 'string',
			render: (row: any) => new Date(row.createdAt).toLocaleString()
		},
		{ key: 'reporterId', header: 'Reporter', sortable: true, sortType: 'string' },
		{
			key: 'counters',
			header: 'Counter Volumes',
			render: (row: any) => {
				const cnts = row.counters || {};
				return Object.entries(cnts).map(([ptId, val]) => {
					const name = ptMap.get(ptId) || ptId;
					return `<div style="font-size:0.875rem;">${name}: <strong>${(val as number).toLocaleString()}</strong></div>`;
				}).join('');
			}
		},
		{
			key: 'status',
			header: 'Status',
			render: (row: any) => {
				if (row.isOverridden) {
					return `<s-badge tone="attention">OVERRIDDEN</s-badge>`;
				}
				return `<s-badge tone="success">ACTIVE</s-badge>`;
			}
		},
		{
			key: 'photoUrl',
			header: 'Evidence',
			render: (row: any) => {
				if (row.photoUrl) {
					return `<a href="${row.photoUrl}" target="_blank" style="color:var(--p-color-text-link); text-decoration:underline; font-size:0.875rem;">View Photo</a>`;
				}
				return `<span style="font-size:0.875rem; color:var(--p-color-text-subdued);">None</span>`;
			}
		},
		{
			key: 'actions',
			header: 'Actions',
			render: (row: any) => {
				if (!isManagerOrAdmin) return '';

				let disabledAttr = '';
				if (role === 'manager') {
					const ageDays = (Date.now() - new Date(row.createdAt).getTime()) / (1000 * 60 * 60 * 24);
					if (ageDays > 365) {
						disabledAttr = 'disabled';
					}
				}

				return `
					<div style="display: flex; gap: 0.5rem; align-items: center;">
						<s-button data-action="override" data-id="${row.id}" size="slim" ${disabledAttr}>Override</s-button>
						<s-button data-action="delete" data-id="${row.id}" tone="critical" size="slim" ${disabledAttr}>Delete</s-button>
					</div>
				`;
			}
		}
	];

	try {
		const res = await fetch(`/api/v1/assets/${assetId}/readings`, {
			headers: getAuthHeaders(),
		});
		if (res.ok) {
			table.items = await res.json();
		} else {
			table.items = [];
		}
	} catch (e) {
		console.error('Failed to fetch asset readings', e);
		table.items = [];
	}
}

export function openOverrideModal(item: any) {
	selectedReadingSignal.value = item;

	const errDiv = document.getElementById('override-error-banner');
	if (errDiv) errDiv.style.display = 'none';

	const ptList = pageTypesSignal.value || [];
	const container = document.getElementById('override-counters-container');
	if (container) {
		container.innerHTML = ptList.map(pt => {
			const val = item.counters[pt.id] || 0;
			return `
				<s-number-field 
					label="Counter: ${pt.displayName}" 
					name="counter_${pt.id}" 
					id="override-counter-${pt.id}"
					value="${val}"
					required>
				</s-number-field>
			`;
		}).join('');
	}

	const viewModal = document.getElementById('view-readings-modal') as any;
	if (viewModal && typeof viewModal.hideOverlay === 'function') {
		viewModal.hideOverlay();
	}

	const overrideModal = document.getElementById('override-reading-modal') as any;
	if (overrideModal && typeof overrideModal.showOverlay === 'function') {
		overrideModal.showOverlay();
	}
}

export function openDeleteReadingModal(id: string) {
	selectedReadingIdSignal.value = id;

	const errDiv = document.getElementById('delete-reading-error-banner');
	if (errDiv) errDiv.style.display = 'none';

	const viewModal = document.getElementById('view-readings-modal') as any;
	if (viewModal && typeof viewModal.hideOverlay === 'function') {
		viewModal.hideOverlay();
	}

	const deleteModal = document.getElementById('delete-reading-confirm-modal') as any;
	if (deleteModal && typeof deleteModal.showOverlay === 'function') {
		deleteModal.showOverlay();
	}
}

export async function initAssetsSignals() {
	try {
		// Fetch core metadata
		const h = getAuthHeaders();

		const resCustomersAll = await fetch('/api/v1/customers', { headers: h });
		if (resCustomersAll.ok) allCustomersSignal.value = await resCustomersAll.json();

		await refreshCustomerPage(1, customersPageMetaSignal.value.pageSize, '');
		await refreshExplorerCustomerPage(1, explorerCustomersMetaSignal.value.pageSize, '');

		const resSites = await fetch('/api/v1/sites', { headers: h });
		if (resSites.ok) sitesSignal.value = await resSites.json();

		const resModels = await fetch('/api/v1/device-models', { headers: h });
		if (resModels.ok) deviceModelsSignal.value = await resModels.json();

		const resPageTypes = await fetch('/api/v1/page-types', { headers: h });
		if (resPageTypes.ok) pageTypesSignal.value = await resPageTypes.json();

		await refreshAssets();
	} catch (e) {
		console.error('Failed to initialize page signals', e);
	}
}

function renderAssetTypeFilterTabs(activeFilter: 'all' | 'mps' | 'non-mps', counts: Record<'all' | 'mps' | 'non-mps', number>): string {
	const tabs = [
		{ key: 'all', label: 'All Fleet', id: 'btn-filter-mps-all' },
		{ key: 'mps', label: 'MPS', id: 'btn-filter-mps-only' },
		{ key: 'non-mps', label: 'Non-MPS', id: 'btn-filter-mps-non' },
	] as const;

	return tabs.map(tab => {
		const isActive = activeFilter === tab.key;
		return `
			<button
				class="ticket-filter-tab ${isActive ? 'ticket-filter-tab--active' : ''}"
				data-filter="${tab.key}"
				id="${tab.id}"
				type="button"
			>
				${tab.label}
				<span class="ticket-filter-count">${counts[tab.key]}</span>
			</button>
		`;
	}).join('');
}

export function renderAssetsPage(): string {
	const mpsFilter = assetsMpsFilterSignal.value;
	const fleetAssets = assetsListSignal.value || [];
	const assetTypeCounts = {
		all: fleetAssets.length,
		mps: fleetAssets.filter(a => a.isMps === true).length,
		'non-mps': fleetAssets.filter(a => a.isMps === false).length,
	};
	let assets = fleetAssets;
	if (mpsFilter === 'mps') {
		assets = assets.filter(a => a.isMps === true);
	} else if (mpsFilter === 'non-mps') {
		assets = assets.filter(a => a.isMps === false);
	}
	const customers = explorerCustomersSignal.value || [];
	const sites = sitesSignal.value || [];
	const models = deviceModelsSignal.value || [];
	const pageTypes = pageTypesSignal.value || [];

	// Build lookup maps for displaying names
	const modelMap = new Map(models.map(m => [m.id, `${m.vendor} ${m.modelName}`]));
	const siteMap = new Map(sites.map(s => [s.id, s]));

	const role = activeRoleSignal.value;
	const isManagerPlus = role === 'superadmin' || role === 'admin' || role === 'manager';

	const level = explorerLevelSignal.value;
	const selectedCustomerId = selectedExplorerCustomerIdSignal.value;
	const selectedDeptId = selectedExplorerDepartmentIdSignal.value;

	let mainSectionHtml = '';
	let breadcrumbHtml = '';

	if (level === 'customers') {
		breadcrumbHtml = ``;
		const kpiRowHtml = isManagerPlus ? `
			<s-section>
				<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Total Managed Assets</s-text>
							<s-heading element="h2">${assets.length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Active Machines</s-text>
							<s-heading element="h2">${assets.filter(a => a.status === 'active').length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Total Active Sites</s-text>
							<s-heading element="h2">${sites.length}</s-heading>
						</s-stack>
					</s-box>
				</s-grid>
			</s-section>
		` : '';

		mainSectionHtml = `
			${kpiRowHtml}
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<opm-data-table id="explorer-customers-table" searchable="true" paginated="true" page-size="10"></opm-data-table>
				</s-box>
			</s-section>
		`;

	} else if (level === 'departments') {
		const customerName = getCustomerName(selectedCustomerId);

		breadcrumbHtml = `
			<div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem;">
				<a href="#" id="breadcrumb-all-customers" style="color:var(--p-color-text-link); text-decoration:none; font-size:0.875rem; font-weight:500;">All Customers</a>
				<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
				<s-text style="font-weight:600; font-size:0.875rem;">${customerName}</s-text>
			</div>
		`;

		const customerAssets = assets.filter(a => a.customerId === selectedCustomerId);
		const depts = departmentsForExplorerSignal.value || [];

		const kpiRowHtml = isManagerPlus ? `
			<s-section>
				<s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Total Customer Assets</s-text>
							<s-heading element="h2">${customerAssets.length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Active Machines</s-text>
							<s-heading element="h2">${customerAssets.filter(a => a.status === 'active').length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Errors / Alerts</s-text>
							<s-heading element="h2">${customerAssets.filter(a => a.status === 'error').length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Departments</s-text>
							<s-heading element="h2">${depts.length}</s-heading>
						</s-stack>
					</s-box>
				</s-grid>
			</s-section>
		` : '';

		const deptCardsHtml = depts.map(d => {
			const deptAssets = customerAssets.filter(a => d.siteIds && d.siteIds.includes(a.siteId));
			const deptAssetCount = deptAssets.length;
			const assignedSiteBadges = (d.siteIds || []).map((sID: string) => {
				const sObj = sites.find(s => s.id === sID);
				return sObj ? `<s-badge>${sObj.name}</s-badge>` : '';
			}).join(' ');

			return `
				<s-box padding="base" background="surface" border="base" borderRadius="base" 
				       id="explorer-dept-${d.id}" style="cursor:pointer;">
					<s-stack gap="small">
						<s-heading element="h3">${d.name}</s-heading>
						<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">
							${assignedSiteBadges}
						</div>
						<s-badge tone="info">${deptAssetCount} Assets</s-badge>
					</s-stack>
				</s-box>
			`;
		}).join('');

		mainSectionHtml = `
			${kpiRowHtml}
			<s-section>
				<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
					<!-- "All Assets" quick card -->
					<s-box padding="base" background="surface" border="base" borderRadius="base" 
					       id="explorer-all-customer-assets" style="cursor:pointer;">
						<s-stack gap="small">
							<s-heading element="h3">All Assets</s-heading>
							<s-text tone="subdued">View entire customer fleet</s-text>
							<s-badge tone="info">${customerAssets.length} machines</s-badge>
						</s-stack>
					</s-box>
					${deptCardsHtml}
				</s-grid>
			</s-section>
		`;

	} else if (level === 'assets') {
		const customerName = getCustomerName(selectedCustomerId);

		let departmentName = '';
		if (selectedDeptId) {
			const activeDeptObj = departmentsForExplorerSignal.value.find(d => d.id === selectedDeptId);
			if (activeDeptObj) departmentName = activeDeptObj.name;
		}

		breadcrumbHtml = `
			<div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem;">
				<a href="#" id="breadcrumb-all-customers" style="color:var(--p-color-text-link); text-decoration:none; font-size:0.875rem; font-weight:500;">All Customers</a>
				<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
				<a href="#" id="breadcrumb-customer" style="color:var(--p-color-text-link); text-decoration:none; font-size:0.875rem; font-weight:500;">${customerName}</a>
				<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
				<s-text style="font-weight:600; font-size:0.875rem;">${departmentName || 'All Assets'}</s-text>
			</div>
		`;

		// Filter assets to the current context
		const customerAssets = assets.filter(a => a.customerId === selectedCustomerId);
		let filteredAssets = customerAssets;

		if (selectedDeptId) {
			const activeDeptObj = departmentsForExplorerSignal.value.find(d => d.id === selectedDeptId);
			if (activeDeptObj && activeDeptObj.siteIds) {
				filteredAssets = customerAssets.filter(a => activeDeptObj.siteIds.includes(a.siteId));
			}
		}

		// Apply top-level filters (Site, Status) if they are set on the pre-filtered list
		const statusFilter = activeFiltersSignal.value.status;
		const siteFilter = activeFiltersSignal.value.siteId;

		if (siteFilter && siteFilter !== 'all') {
			filteredAssets = filteredAssets.filter(a => a.siteId === siteFilter);
		}
		if (statusFilter && statusFilter !== 'all') {
			filteredAssets = filteredAssets.filter(a => a.status === statusFilter);
		}

		// Generate table rows for filteredAssets
		const tableRowsHtml = filteredAssets.map(a => {
			const modelName = modelMap.get(a.modelId) || 'Unknown Model';
			const siteObj = siteMap.get(a.siteId);
			const siteName = siteObj ? siteObj.name : 'Unknown Site';
			const gpsStr = siteObj && siteObj.latitude && siteObj.longitude
				? `(${siteObj.latitude.toFixed(4)}, ${siteObj.longitude.toFixed(4)})`
				: '';

			let badgeTone = 'info';
			if (a.status === 'active') badgeTone = 'success';
			if (a.status === 'error') badgeTone = 'critical';

			const isRetired = a.status === 'retired';

			return `
				<s-table-row>
					<s-table-cell><s-link href="#machine-history?id=${encodeURIComponent(a.id)}&customerId=${encodeURIComponent(selectedCustomerId || '')}${selectedDeptId ? `&deptId=${encodeURIComponent(selectedDeptId)}` : ''}"><strong>${escapeHtml(a.serialNumber)}</strong></s-link></s-table-cell>
					<s-table-cell>${modelName}</s-table-cell>
					<s-table-cell>
						<s-stack gap="none">
							<span>${siteName}</span>
							<span style="font-size: 0.8rem; color: var(--p-color-text-subdued);">${gpsStr}</span>
						</s-stack>
					</s-table-cell>
					<s-table-cell><s-badge tone="${badgeTone}">${a.status}</s-badge></s-table-cell>
					<s-table-cell>${a.isMps === false ? '<s-badge tone="attention">SERVICE ONLY</s-badge>' : '<s-badge tone="info">MPS</s-badge>'}</s-table-cell>
					<s-table-cell>${a.ownershipType}</s-table-cell>
					<s-table-cell>${a.anomalySensitivityMultiplier || 3.0}x</s-table-cell>
					<s-table-cell>
							<s-button href="#machine-history?id=${encodeURIComponent(a.id)}&customerId=${encodeURIComponent(selectedCustomerId || '')}${selectedDeptId ? `&deptId=${encodeURIComponent(selectedDeptId)}` : ''}" variant="primary" size="slim">View history</s-button>
							${isManagerPlus ? `<s-button id="btn-manual-reading-${a.id}" variant="secondary" size="slim">Add Reading</s-button><s-button id="btn-view-readings-${a.id}" size="slim">Readings</s-button><s-button id="btn-view-audit-${a.id}" size="slim">View Audit</s-button><s-button id="btn-edit-asset-${a.id}" size="slim">Edit</s-button>${!isRetired ? `<s-button id="btn-retire-asset-${a.id}" tone="critical" size="slim">Retire</s-button>` : ''}` : ''}
					</s-table-cell>
				</s-table-row>
			`;
		}).join('');

		mainSectionHtml = `

			<!-- Fleet Table Section -->
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${filteredAssets.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No assets matched the active criteria in this section.</s-text>
						</div>
					` : `
						<s-table>
							<s-table-header-row>
								<s-table-header>Serial Number</s-table-header>
								<s-table-header>Model</s-table-header>
								<s-table-header>Location</s-table-header>
								<s-table-header>Status</s-table-header>
								<s-table-header>MPS</s-table-header>
								<s-table-header>Ownership</s-table-header>
								<s-table-header>Sensitivity</s-table-header>
								<s-table-header>Actions</s-table-header>
							</s-table-header-row>
							<s-table-body>
								${tableRowsHtml}
							</s-table-body>
						</s-table>
					`}
				</s-box>
			</s-section>
		`;
	}

	// Selection options for Customer & Site in Registration Modal
	const selectedRegCustId = registrationSelectedCustomerIdSignal.value;
	const filteredSitesForReg = sites.filter(s => s.customerId === selectedRegCustId);
	const regSiteOptionsHtml = filteredSitesForReg.map(s => `<option value="${s.id}">${s.name}</option>`).join('');

	const firstRegSite = filteredSitesForReg[0];
	const regGpsStr = firstRegSite && firstRegSite.latitude && firstRegSite.longitude
		? `Site GPS: ${firstRegSite.latitude.toFixed(4)}, ${firstRegSite.longitude.toFixed(4)}`
		: 'No coordinates available';

	const regModelOptionsHtml = models.map(m => `<option value="${m.id}">${m.vendor} ${m.modelName}</option>`).join('');

	return `
		<style>
			.asset-explorer-header {
				padding: 1.25rem 1.5rem 0;
				background: var(--p-color-bg-surface, #ffffff);
				border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
			}

			.asset-explorer-title-row {
				display: flex;
				align-items: center;
				gap: 0.75rem;
				flex-wrap: wrap;
			}

			.asset-explorer-title-row s-heading {
				font-size: 1.25rem;
				font-weight: 700;
				margin: 0;
			}

			.asset-explorer-actions {
				margin-left: auto;
				margin-bottom: 10px;
			}

			.asset-explorer-breadcrumb {
				padding: 0.25rem 0 0.75rem;
			}

			.ticket-filter-bar {
				display: flex;
				gap: 0.25rem;
				flex-wrap: wrap;
				padding: 0.75rem 1.25rem;
				background: var(--p-color-bg-surface, #ffffff);
				border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
			}

			.ticket-filter-tab {
				display: inline-flex;
				align-items: center;
				gap: 0.35rem;
				padding: 0.3rem 0.65rem;
				font-size: 0.8rem;
				font-weight: 500;
				border: 1px solid var(--p-color-border-subdued, #c4cdd5);
				border-radius: var(--p-border-radius-200, 8px);
				background: transparent;
				color: var(--p-color-text-subdued, #6d7175);
				cursor: pointer;
				transition: all 0.15s ease;
				font-family: inherit;
			}

			.ticket-filter-tab:hover {
				background: var(--p-color-bg-action-hover, rgba(0,0,0,0.05));
				color: var(--p-color-text, #202223);
			}

			.ticket-filter-tab--active {
				background: var(--p-color-bg-surface-selected, #e4e6eb);
				color: var(--p-color-text, #202223);
				border-color: var(--p-color-border-accent, #008060);
				font-weight: 600;
			}

			.ticket-filter-count {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: 18px;
				height: 18px;
				font-size: 0.7rem;
				font-weight: 600;
				background: var(--p-color-bg-surface-secondary, #f1f2f4);
				border-radius: 9px;
				padding: 0 4px;
			}

			.ticket-filter-tab--active .ticket-filter-count {
				background: var(--p-color-bg-action-primary, #008060);
				color: white;
			}

			.asset-filter-summary {
				display: inline-flex;
				align-items: center;
				margin-left: auto;
				padding-left: 0.75rem;
			}
		</style>
    <div style="display:flex;flex-direction:column;height:100%;">
      <!-- Page Header -->
      <div style="padding:1.25rem 1.5rem 0;background:var(--p-color-bg-surface, #ffffff);border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">
        <s-stack direction="inline" gap="base" align="center" wrap>
					<s-heading element="h1">Asset Explorer</s-heading>
					<s-badge tone="info">${assets.length} active</s-badge>
					<div class="asset-explorer-actions">
						<s-button variant="primary" id="btn-open-register">Register Asset</s-button>
					</div>
				</s-stack>
				<div class="asset-explorer-breadcrumb" id="explorer-breadcrumb-slot">
					${breadcrumbHtml}
				</div>
				</div>

			<div class="ticket-filter-bar" id="ticket-filter-bar-container">
				${renderAssetTypeFilterTabs(mpsFilter, assetTypeCounts)}
				<div class="asset-filter-summary">
					<s-text tone="subdued" style="font-size: 0.875rem;">
						Showing <strong>${assets.length}</strong> active managed assets
					</s-text>
				</div>
			</div>

			<!-- Map Section (rendered at all levels) -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<s-stack gap="base">
						<div style="height: 380px; width: 100%; position: relative; border-radius: 8px; overflow: hidden; border: 1px solid var(--p-color-border-subdued,#e1e3e5);">
							<opm-asset-map id="fleet-map"></opm-asset-map>
						</div>
					</s-stack>
				</s-box>
			</s-section>

			<!-- Dynamic Hierarchical Main Section -->
			<div id="explorer-main-content">
				${mainSectionHtml}
			</div>

			<!-- All Modals (sitting safely outside the main layout) -->
			<!-- Registration Modal -->
			<s-modal id="register-asset-modal" heading="Register New Fleet Asset">
				<s-form id="form-register-asset">
					<s-stack gap="base">
						<s-text-field label="Serial Number" name="serialNumber" placeholder="e.g. SN-HP-9876" required></s-text-field>
						
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Customer</label>
							<select name="customerId" id="reg-customer-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
								${customers.map(c => `<option value="${c.id}" ${c.id === selectedRegCustId ? 'selected' : ''}>${c.name}</option>`).join('')}
							</select>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Assign Site</label>
							<select name="siteId" id="reg-site-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
								${regSiteOptionsHtml}
							</select>
							<s-text tone="subdued" id="reg-site-gps">${regGpsStr}</s-text>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Device Model</label>
							<select name="modelId" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
								${regModelOptionsHtml}
							</select>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Ownership Structure</label>
							<select name="ownershipType" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
								<option value="owned">Owned</option>
								<option value="rented" selected>Rented (MPS Contract)</option>
							</select>
						</div>

						<s-number-field label="Polling Interval (seconds)" name="intervalSeconds" value="3600" required></s-number-field>
						<s-number-field label="Anomaly Sensitivity Multiplier" name="anomalySensitivityMultiplier" value="3.0" required></s-number-field>

						<div id="register-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem;"></div>

						<s-stack direction="inline" gap="small" style="margin-top:1rem;">
							<s-button variant="primary" id="btn-submit-register">Register</s-button>
							<s-button commandFor="register-asset-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Retire Confirmation Modal -->
			<s-modal id="retire-confirm-modal" heading="Retire Fleet Asset?">
				<s-stack gap="base">
					<s-text tone="critical">Warning: Retiring this asset preserves its historical counter data for billing logs but changes its status to retired, disabling active polling tasks.</s-text>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" id="confirm-retire-btn">Confirm Retirement</s-button>
						<s-button commandFor="retire-confirm-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Manual Readings Submission Modal -->
			<s-modal id="manual-reading-modal" heading="Record Physical Meter Reading">
				<s-form id="form-manual-reading">
					<s-stack gap="base">
						<s-text>Input current cumulative meter counters observed on the device.</s-text>
						
						<!-- Dynamic Counters -->
						${pageTypes.map(pt => `
							<s-number-field 
								label="Cumulative Count: ${pt.displayName}" 
								name="counter_${pt.id}" 
								id="input-counter-${pt.id}"
								placeholder="e.g. 15000"
								required>
							</s-number-field>
						`).join('')}

						<s-drop-zone 
							label="Photo Evidence" 
							name="photo" 
							id="reading-photo-dropzone" 
							accept="image/*"
							required>
							<s-stack gap="small" alignment="center" style="padding: 1rem; text-align: center; cursor: pointer; width: 100%;">
								<s-icon type="image" tone="subdued"></s-icon>
								<s-text tone="subdued">Drag and drop an image of the printer screen here, or click to browse</s-text>
								<s-text tone="subdued" style="font-size: 0.8rem;">Maximum file size: 10MB (PNG, JPG, JPEG, WEBP)</s-text>
							</s-stack>
						</s-drop-zone>

						<div id="reading-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem;"></div>

						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-reading">Submit Meter Log</s-button>
							<s-button commandFor="manual-reading-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- View Audit Modal -->
			<s-modal id="view-audit-modal" heading="Asset Audit Logs">
				<s-stack gap="base">
					<s-text>Audit history for asset: <strong id="audit-asset-serial"></strong></s-text>
					<div id="audit-log-timeline" style="margin-top: 1rem; border-left: 2px solid var(--p-color-border-subdued); padding-left: 1rem; display: flex; flex-direction: column; gap: 1rem;">
						<!-- Populated dynamically -->
					</div>
					<s-stack direction="inline" gap="small" style="margin-top: 1.5rem;">
						<s-button commandFor="view-audit-modal" command="--hide">Close</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Edit Asset Modal -->
			<s-modal id="edit-asset-modal" heading="Edit Asset Details">
				<s-form id="form-edit-asset">
					<s-stack gap="base">
						<s-text-field label="Serial Number" name="editSerialNumber" id="edit-serial-number" readonly></s-text-field>
						<s-number-field label="Polling Interval (seconds)" name="editIntervalSeconds" id="edit-interval-seconds" required></s-number-field>
						<s-number-field label="Anomaly Sensitivity Multiplier" name="editAnomalyMultiplier" id="edit-anomaly-multiplier" required></s-number-field>
						
						<div id="edit-success-banner" style="display:none; color:var(--p-color-text-success); margin-top:0.5rem; font-weight:500;">Changes saved successfully.</div>

						<s-stack direction="inline" gap="small" style="margin-top: 1.5rem;">
							<s-button variant="primary" id="btn-submit-edit">Save Changes</s-button>
							<s-button commandFor="edit-asset-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Readings Log Modal -->
			<s-modal id="view-readings-modal" heading="Device Readings Log" style="--s-modal-max-width: 800px;">
				<s-stack gap="base">
					<s-text>Recorded telemetry and manual counters for asset: <strong id="readings-asset-serial"></strong></s-text>
					<s-box padding="none" background="surface" border="base" borderRadius="base">
						<opm-data-table
							id="readings-log-table"
							searchable="false"
							paginated="true"
							page-size="5"
							empty-message="No readings recorded for this device."
						></opm-data-table>
					</s-box>
					<s-stack direction="inline" gap="small" style="margin-top: 1.5rem; justify-content: flex-end;">
						<s-button id="btn-close-readings" commandFor="view-readings-modal" command="--hide">Close</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Override Reading Modal -->
			<s-modal id="override-reading-modal" heading="Override Meter Reading">
				<s-form id="form-override-reading">
					<s-stack gap="base">
						<s-text>Adjust counter values for this reading. Changing these values will trigger billing recalculations if within an active contract cycle.</s-text>
						
						<!-- Dynamic Counters will be injected here by JS -->
						<div id="override-counters-container" style="display: flex; flex-direction: column; gap: 1rem;"></div>

						<div id="override-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-weight:500;"></div>

						<s-stack direction="inline" gap="small" style="margin-top: 1.5rem;">
							<s-button variant="primary" id="btn-submit-override">Submit Override</s-button>
							<s-button id="btn-cancel-override">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete Reading Confirmation Modal -->
			<s-modal id="delete-reading-confirm-modal" heading="Delete Meter Reading?">
				<s-stack gap="base">
					<s-text tone="critical" style="font-weight: 500;">Warning: Deleting this reading is permanent and will trigger billing rollup recalculations in Nepali Rupees (NRs.) for affected cycles.</s-text>
					<s-text>Are you sure you want to delete this recorded reading?</s-text>
					<div id="delete-reading-error-banner" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-weight:500;"></div>
					<s-stack direction="inline" gap="small" style="margin-top: 1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-reading">Confirm Delete</s-button>
						<s-button id="btn-cancel-delete-reading">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
	`;
}

export function setupAssetsPageListeners() {
	// Bind MPS Segment Filter Buttons
	const btnAll = document.getElementById('btn-filter-mps-all');
	if (btnAll) {
		btnAll.addEventListener('click', () => {
			assetsMpsFilterSignal.value = 'all';
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem('opm_assets_mps_filter', 'all');
			}
		});
	}
	const btnMps = document.getElementById('btn-filter-mps-only');
	if (btnMps) {
		btnMps.addEventListener('click', () => {
			assetsMpsFilterSignal.value = 'mps';
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem('opm_assets_mps_filter', 'mps');
			}
		});
	}
	const btnNon = document.getElementById('btn-filter-mps-non');
	if (btnNon) {
		btnNon.addEventListener('click', () => {
			assetsMpsFilterSignal.value = 'non-mps';
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem('opm_assets_mps_filter', 'non-mps');
			}
		});
	}

	let selectedFile: File | null = null;

	function resetDropzoneUI() {
		const dropzone = document.getElementById('reading-photo-dropzone') as any;
		if (!dropzone) return;
		dropzone.value = null;
		dropzone.removeAttribute('error');
		dropzone.innerHTML = `
			<s-stack gap="small" alignment="center" style="padding: 1rem; text-align: center; cursor: pointer; width: 100%;">
				<s-icon type="image" tone="subdued"></s-icon>
				<s-text tone="subdued">Drag and drop an image of the printer screen here, or click to browse</s-text>
				<s-text tone="subdued" style="font-size: 0.8rem;">Maximum file size: 10MB (PNG, JPG, JPEG, WEBP)</s-text>
			</s-stack>
		`;
	}

	function updateDropzoneUI(file: File) {
		const dropzone = document.getElementById('reading-photo-dropzone') as any;
		if (!dropzone) return;
		dropzone.innerHTML = `
			<s-stack direction="inline" gap="base" alignment="center" style="padding: 1rem; width: 100%;">
				<s-thumbnail size="medium" alt="${file.name}" src="${URL.createObjectURL(file)}"></s-thumbnail>
				<s-stack gap="none" style="flex: 1;">
					<s-text><strong>${file.name}</strong></s-text>
					<s-text tone="subdued" style="font-size: 0.8rem;">${(file.size / (1024 * 1024)).toFixed(2)} MB</s-text>
				</s-stack>
				<s-button id="btn-clear-photo" variant="plain" tone="critical">Remove</s-button>
			</s-stack>
		`;
		const clearBtn = document.getElementById('btn-clear-photo');
		if (clearBtn) {
			clearBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				selectedFile = null;
				resetDropzoneUI();
			});
		}
	}
	// 1. Open Register Modal
	const openRegBtn = document.getElementById('btn-open-register');
	if (openRegBtn) {
		openRegBtn.addEventListener('click', () => {
			const modal = document.getElementById('register-asset-modal') as any;
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 2. Customer change inside Registration Modal (filters Site select dropdown)
	const regCustSelect = document.getElementById('reg-customer-select') as HTMLSelectElement;
	if (regCustSelect) {
		regCustSelect.addEventListener('change', (e) => {
			const target = e.currentTarget as HTMLSelectElement;
			registrationSelectedCustomerIdSignal.value = target.value;
			// Trigger re-render to update options
			const route = window.location.hash || '#dashboard';
			if (route === '#assets') {
				const appContainer = document.getElementById('app');
				if (appContainer) {
					// Hack to preserve current page view and redraw registration dropdowns
					// Better way is to update DOM options dynamically
					updateRegSitesDropdown();
				}
			}
		});
	}

	// Site change in registration updates GPS text
	const regSiteSelect = document.getElementById('reg-site-select') as HTMLSelectElement;
	if (regSiteSelect) {
		regSiteSelect.addEventListener('change', updateRegGpsDisplay);
	}

	// 3. Filters triggers
	const filterCust = document.getElementById('filter-customer') as HTMLSelectElement;
	if (filterCust) {
		filterCust.value = activeFiltersSignal.value.customerId;
		filterCust.addEventListener('change', (e) => {
			const val = (e.currentTarget as HTMLSelectElement).value;
			activeFiltersSignal.value = {
				...activeFiltersSignal.value,
				customerId: val,
				siteId: 'all', // Reset site filter when customer changes
			};
			refreshAssets().then(() => triggerAppReRender());
		});
	}

	const filterSite = document.getElementById('filter-site') as HTMLSelectElement;
	if (filterSite) {
		filterSite.value = activeFiltersSignal.value.siteId;
		filterSite.addEventListener('change', (e) => {
			const val = (e.currentTarget as HTMLSelectElement).value;
			activeFiltersSignal.value = {
				...activeFiltersSignal.value,
				siteId: val,
			};
			refreshAssets().then(() => triggerAppReRender());
		});
	}

	const filterStatus = document.getElementById('filter-status') as HTMLSelectElement;
	if (filterStatus) {
		filterStatus.value = activeFiltersSignal.value.status;
		filterStatus.addEventListener('change', (e) => {
			const val = (e.currentTarget as HTMLSelectElement).value;
			activeFiltersSignal.value = {
				...activeFiltersSignal.value,
				status: val,
			};
			refreshAssets().then(() => triggerAppReRender());
		});
	}

	// 4. Register Asset form submit
	const submitRegBtn = document.getElementById('btn-submit-register');
	if (submitRegBtn) {
		submitRegBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-register-asset');
			if (!form) return;

			// Map values
			const body = {
				serialNumber: (form.querySelector('[name="serialNumber"]') as any)?.value || '',
				modelId: (form.querySelector('[name="modelId"]') as any)?.value || '',
				customerId: (form.querySelector('[name="customerId"]') as any)?.value || '',
				siteId: (form.querySelector('[name="siteId"]') as any)?.value || '',
				ownershipType: (form.querySelector('[name="ownershipType"]') as any)?.value || '',
				intervalSeconds: Number((form.querySelector('[name="intervalSeconds"]') as any)?.value || 3600),
				anomalySensitivityMultiplier: Number((form.querySelector('[name="anomalySensitivityMultiplier"]') as any)?.value || 3.0),
				isMps: true,
			};

			const banner = document.getElementById('register-error-banner');
			if (banner) banner.style.display = 'none';

			try {
				const res = await fetch('/api/v1/assets', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					// Hide modal
					const modal = document.getElementById('register-asset-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					// Refresh and redraw
					await refreshAssets();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (banner) {
						banner.textContent = data.error || 'Failed to register asset.';
						banner.style.display = 'block';
					}
				}
			} catch (err) {
				if (banner) {
					banner.textContent = 'Error: network failure.';
					banner.style.display = 'block';
				}
			}
		});
	}

	// 5. Assets row action triggers (Retire & Manual Reading)
	const assets = assetsListSignal.value || [];
	assets.forEach(a => {
		const retireBtn = document.getElementById(`btn-retire-asset-${a.id}`);
		if (retireBtn) {
			retireBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const modal = document.getElementById('retire-confirm-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const readingBtn = document.getElementById(`btn-manual-reading-${a.id}`);
		if (readingBtn) {
			readingBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				// Reset form errors/values
				const banner = document.getElementById('reading-error-banner');
				if (banner) banner.style.display = 'none';

				// Reset selected photo file
				selectedFile = null;
				resetDropzoneUI();

				const modal = document.getElementById('manual-reading-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const viewReadingsBtn = document.getElementById(`btn-view-readings-${a.id}`);
		if (viewReadingsBtn) {
			viewReadingsBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const serialEl = document.getElementById('readings-asset-serial');
				if (serialEl) serialEl.textContent = a.serialNumber;

				refreshAssetReadings(a.id);

				const modal = document.getElementById('view-readings-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

	});

	// Confirm Retire Button
	const confirmRetireBtn = document.getElementById('confirm-retire-btn');
	if (confirmRetireBtn) {
		confirmRetireBtn.addEventListener('click', async () => {
			const id = selectedAssetIdSignal.value;
			if (!id) return;

			try {
				const res = await fetch(`/api/v1/assets/${id}/retire`, {
					method: 'POST',
					headers: getAuthHeaders(),
				});
				if (res.ok) {
					const modal = document.getElementById('retire-confirm-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshAssets();
					triggerAppReRender();
				}
			} catch (err) {
				console.error('Failed to retire asset', err);
			}
		});
	}

	// Dropzone file change listener
	const dropzone = document.getElementById('reading-photo-dropzone') as any;
	if (dropzone) {
		dropzone.addEventListener('change', (e: any) => {
			const files = e.currentTarget.files || [];
			const file = files[0];
			if (file) {
				if (file.size > 10 * 1024 * 1024) {
					dropzone.setAttribute('error', 'File size exceeds the 10MB limit.');
					return;
				}
				if (!file.type.startsWith('image/')) {
					dropzone.setAttribute('error', 'Only image files (PNG, JPG, JPEG, WEBP) are allowed.');
					return;
				}
				dropzone.removeAttribute('error');
				selectedFile = file;
				updateDropzoneUI(file);
			}
		});
	}

	// Submit manual reading log button
	const submitReadingBtn = document.getElementById('btn-submit-reading');
	if (submitReadingBtn) {
		submitReadingBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-manual-reading');
			if (!form) return;

			const id = selectedAssetIdSignal.value;
			if (!id) return;

			const banner = document.getElementById('reading-error-banner');
			if (banner) banner.style.display = 'none';

			// Validate photo evidence upload is present
			if (!selectedFile) {
				const dz = document.getElementById('reading-photo-dropzone') as any;
				if (dz) {
					dz.setAttribute('error', 'Photo evidence is required for manual entries.');
				}
				if (banner) {
					banner.textContent = 'Please upload a photo showing the current counter volumes.';
					banner.style.display = 'block';
				}
				return;
			}

			// Map dynamic counters
			const counters: { [key: string]: number } = {};
			const pageTypes = pageTypesSignal.value || [];
			let missingCounters = false;
			pageTypes.forEach(pt => {
				const valStr = (form.querySelector(`[name="counter_${pt.id}"]`) as any)?.value;
				if (valStr) {
					counters[pt.id] = Number(valStr);
				} else {
					missingCounters = true;
					const el = document.getElementById(`input-counter-${pt.id}`) as any;
					if (el) el.setAttribute('error', 'This counter is required.');
				}
			});

			if (missingCounters) {
				if (banner) {
					banner.textContent = 'All counters are required.';
					banner.style.display = 'block';
				}
				return;
			}

			submitReadingBtn.setAttribute('loading', 'true');
			submitReadingBtn.setAttribute('disabled', 'true');

			try {
				// 1. Upload photo first
				const formData = new FormData();
				formData.append('file', selectedFile);

				const authHeaders = getAuthHeaders();
				const uploadHeaders: Record<string, string> = {};
				if (authHeaders['Authorization']) {
					uploadHeaders['Authorization'] = authHeaders['Authorization'];
				}

				const uploadRes = await fetch('/api/v1/readings/upload-photo', {
					method: 'POST',
					headers: uploadHeaders,
					body: formData,
				});

				if (!uploadRes.ok) {
					const errData = await uploadRes.json();
					throw new Error(errData.error || 'Failed to upload photo evidence.');
				}

				const { photoUrl } = await uploadRes.json();

				// 2. Submit manual reading log
				const body = {
					assetId: id,
					counters: counters,
					photoUrl: photoUrl,
				};

				const res = await fetch('/api/v1/readings/manual', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('manual-reading-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshAssets();
					triggerAppReRender();
				} else {
					const data = await res.json();
					const errMsg = data.error || 'Failed to submit meter log.';

					if (banner) {
						banner.textContent = errMsg;
						banner.style.display = 'block';
					}

					// Map counter reduction validation exception back to specific fields if matched
					if (errMsg.toLowerCase().includes('volume decreased') || errMsg.toLowerCase().includes('monotonic')) {
						pageTypes.forEach(pt => {
							const el = document.getElementById(`input-counter-${pt.id}`) as any;
							if (el) {
								el.setAttribute('error', 'Reading volume cannot decrease below previous values.');
							}
						});
					}
				}
			} catch (err: any) {
				if (banner) {
					banner.textContent = err.message || 'Error: network failure.';
					banner.style.display = 'block';
				}
			} finally {
				submitReadingBtn.removeAttribute('loading');
				submitReadingBtn.removeAttribute('disabled');
			}
		});
	}

	// 6. View Audit & Edit button click events
	assets.forEach(a => {
		const viewAuditBtn = document.getElementById(`btn-view-audit-${a.id}`);
		if (viewAuditBtn) {
			viewAuditBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const serialEl = document.getElementById('audit-asset-serial');
				if (serialEl) serialEl.textContent = a.serialNumber;

				const timelineEl = document.getElementById('audit-log-timeline');
				if (timelineEl) {
					let timelineHtml = `
						<div style="position: relative; padding-bottom: 0.5rem;">
							<div style="position: absolute; left: -1.35rem; top: 0.25rem; width: 8px; height: 8px; border-radius: 50%; background: var(--p-color-bg-interactive-selected);"></div>
							<s-text><strong>Asset Registered</strong></s-text>
							<s-text tone="subdued" style="font-size: 0.8rem; display: block; margin-top: 0.25rem;">Serial number ${a.serialNumber} registered under ownership type ${a.ownershipType}.</s-text>
						</div>
					`;
					if (a.status === 'retired') {
						timelineHtml += `
							<div style="position: relative; padding-bottom: 0.5rem;">
								<div style="position: absolute; left: -1.35rem; top: 0.25rem; width: 8px; height: 8px; border-radius: 50%; background: var(--p-color-text-critical);"></div>
								<s-text><strong>Asset Retired</strong></s-text>
								<s-text tone="subdued" style="font-size: 0.8rem; display: block; margin-top: 0.25rem;">Status changed to retired. Active polling disabled.</s-text>
							</div>
						`;
					}
					timelineEl.innerHTML = timelineHtml;
				}

				const modal = document.getElementById('view-audit-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const editBtn = document.getElementById(`btn-edit-asset-${a.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;

				const serialInput = document.getElementById('edit-serial-number') as any;
				if (serialInput) serialInput.value = a.serialNumber;

				const intervalInput = document.getElementById('edit-interval-seconds') as any;
				if (intervalInput) intervalInput.value = (a.intervalSeconds || 3600).toString();

				const sensitivityInput = document.getElementById('edit-anomaly-multiplier') as any;
				if (sensitivityInput) sensitivityInput.value = (a.anomalySensitivityMultiplier || 3.0).toString();

				const banner = document.getElementById('edit-success-banner');
				if (banner) banner.style.display = 'none';

				const modal = document.getElementById('edit-asset-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});

	// Submit Edit Action
	const submitEditBtn = document.getElementById('btn-submit-edit');
	if (submitEditBtn) {
		submitEditBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedAssetIdSignal.value;
			if (!id) return;

			const intervalInput = document.getElementById('edit-interval-seconds') as any;
			const sensitivityInput = document.getElementById('edit-anomaly-multiplier') as any;

			const interval = intervalInput ? Number(intervalInput.value) : 3600;
			const multiplier = sensitivityInput ? Number(sensitivityInput.value) : 3.0;

			const successBanner = document.getElementById('edit-success-banner');

			submitEditBtn.setAttribute('loading', 'true');
			submitEditBtn.setAttribute('disabled', 'true');

			try {
				const res = await fetch(`/api/v1/assets/${id}`, {
					method: 'PATCH',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						intervalSeconds: interval,
						anomalySensitivityMultiplier: multiplier,
					}),
				});

				if (res.ok) {
					// Refresh assets list and local state
					await refreshAssets();
					if (successBanner) {
						successBanner.style.display = 'block';
					}
					setTimeout(() => {
						const modal = document.getElementById('edit-asset-modal') as any;
						if (modal && typeof modal.hideOverlay === 'function') {
							modal.hideOverlay();
						}
						triggerAppReRender();
					}, 800);
				} else {
					const data = await res.json();
					showToast(data.error || 'Failed to update asset.', 'error');
				}
			} catch (err) {
				console.error(err);
				showToast('Network error updating asset.', 'error');
			} finally {
				submitEditBtn.removeAttribute('loading');
				submitEditBtn.removeAttribute('disabled');
			}
		});
	}

	// Readings data table actions listener
	const readingsTable = document.getElementById('readings-log-table') as any;
	if (readingsTable) {
		readingsTable.addEventListener('row-action', (e: CustomEvent) => {
			const { action, id, item } = e.detail;
			if (action === 'override') {
				openOverrideModal(item);
			} else if (action === 'delete') {
				openDeleteReadingModal(id);
			}
		});
	}

	// Override modal Cancel button click handler
	const btnCancelOverride = document.getElementById('btn-cancel-override');
	if (btnCancelOverride) {
		btnCancelOverride.addEventListener('click', (e) => {
			e.preventDefault();
			const modal = document.getElementById('override-reading-modal') as any;
			if (modal && typeof modal.hideOverlay === 'function') {
				modal.hideOverlay();
			}
			const viewModal = document.getElementById('view-readings-modal') as any;
			if (viewModal && typeof viewModal.showOverlay === 'function') {
				viewModal.showOverlay();
			}
		});
	}

	// Override modal Submit button click handler
	const btnSubmitOverride = document.getElementById('btn-submit-override');
	if (btnSubmitOverride) {
		btnSubmitOverride.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-override-reading');
			if (!form) return;

			const reading = selectedReadingSignal.value;
			if (!reading) return;

			const errDiv = document.getElementById('override-error-banner');
			if (errDiv) errDiv.style.display = 'none';

			// Get dynamic counters
			const counters: { [key: string]: number } = {};
			const pageTypes = pageTypesSignal.value || [];
			let invalidInputs = false;

			pageTypes.forEach(pt => {
				const valStr = (form.querySelector(`[name="counter_${pt.id}"]`) as any)?.value;
				if (valStr) {
					const val = Number(valStr);
					if (val < 0) {
						invalidInputs = true;
					}
					counters[pt.id] = val;
				} else {
					invalidInputs = true;
				}
			});

			if (invalidInputs) {
				if (errDiv) {
					errDiv.textContent = 'All counter values must be non-negative integers.';
					errDiv.style.display = 'block';
				}
				return;
			}

			btnSubmitOverride.setAttribute('loading', 'true');
			btnSubmitOverride.setAttribute('disabled', 'true');

			try {
				const res = await fetch(`/api/v1/readings/${reading.id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify({ counters }),
				});

				if (res.ok) {
					const modal = document.getElementById('override-reading-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					const assetId = selectedAssetIdSignal.value;
					if (assetId) {
						await refreshAssetReadings(assetId);
					}
					const viewModal = document.getElementById('view-readings-modal') as any;
					if (viewModal && typeof viewModal.showOverlay === 'function') {
						viewModal.showOverlay();
					}
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to submit override.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err: any) {
				if (errDiv) {
					errDiv.textContent = err.message || 'Error: network failure.';
					errDiv.style.display = 'block';
				}
			} finally {
				btnSubmitOverride.removeAttribute('loading');
				btnSubmitOverride.removeAttribute('disabled');
			}
		});
	}

	// Delete confirmation Cancel button click handler
	const btnCancelDeleteReading = document.getElementById('btn-cancel-delete-reading');
	if (btnCancelDeleteReading) {
		btnCancelDeleteReading.addEventListener('click', (e) => {
			e.preventDefault();
			const modal = document.getElementById('delete-reading-confirm-modal') as any;
			if (modal && typeof modal.hideOverlay === 'function') {
				modal.hideOverlay();
			}
			const viewModal = document.getElementById('view-readings-modal') as any;
			if (viewModal && typeof viewModal.showOverlay === 'function') {
				viewModal.showOverlay();
			}
		});
	}

	// Delete confirmation Confirm button click handler
	const btnConfirmDeleteReading = document.getElementById('btn-confirm-delete-reading');
	if (btnConfirmDeleteReading) {
		btnConfirmDeleteReading.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedReadingIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-reading-error-banner');
			if (errDiv) errDiv.style.display = 'none';

			btnConfirmDeleteReading.setAttribute('loading', 'true');
			btnConfirmDeleteReading.setAttribute('disabled', 'true');

			try {
				const res = await fetch(`/api/v1/readings/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-reading-confirm-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					const assetId = selectedAssetIdSignal.value;
					if (assetId) {
						await refreshAssetReadings(assetId);
					}
					const viewModal = document.getElementById('view-readings-modal') as any;
					if (viewModal && typeof viewModal.showOverlay === 'function') {
						viewModal.showOverlay();
					}
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete reading.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err: any) {
				if (errDiv) {
					errDiv.textContent = err.message || 'Error: network failure.';
					errDiv.style.display = 'block';
				}
			} finally {
				btnConfirmDeleteReading.removeAttribute('loading');
				btnConfirmDeleteReading.removeAttribute('disabled');
			}
		});
	}

	// 7. Dynamic binding, card clicks, breadcrumbs and contextual filtering for opm-asset-map
	const mapEl = document.getElementById('fleet-map') as any;
	if (mapEl) {
		updateMapData();

		mapEl.addEventListener('asset-map-action', (e: CustomEvent) => {
			const { action, id } = e.detail;
			const asset = assetsListSignal.value.find(a => a.id === id);
			if (!asset) return;

			if (action === 'view-history') {
				window.location.hash = `#machine-history?id=${encodeURIComponent(id)}&customerId=${encodeURIComponent(asset.customerId)}${selectedExplorerDepartmentIdSignal.value ? `&deptId=${encodeURIComponent(selectedExplorerDepartmentIdSignal.value)}` : ''}`;
			} else if (action === 'view-readings') {
				selectedAssetIdSignal.value = id;
				const serialEl = document.getElementById('readings-asset-serial');
				if (serialEl) serialEl.textContent = asset.serialNumber;

				refreshAssetReadings(id);

				const modal = document.getElementById('view-readings-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			} else if (action === 'edit-asset') {
				selectedAssetIdSignal.value = id;

				const serialEl = document.getElementById('edit-serial-number') as any;
				if (serialEl) serialEl.value = asset.serialNumber;

				const intervalEl = document.getElementById('edit-interval-seconds') as any;
				if (intervalEl) intervalEl.value = asset.intervalSeconds;

				const anomalyEl = document.getElementById('edit-anomaly-multiplier') as any;
				if (anomalyEl) anomalyEl.value = asset.anomalySensitivityMultiplier;

				const successBanner = document.getElementById('edit-success-banner');
				if (successBanner) successBanner.style.display = 'none';

				const modal = document.getElementById('edit-asset-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			}
		});
	}

	setupExplorerMainSectionListeners();
}

	// 8. Datatable Setup & Listeners for Customers Explorer Level
	const custTable = document.getElementById('explorer-customers-table') as any;
	if (custTable) {
		const customers = allCustomersSignal.value || [];
		custTable.columns = [
			{
				key: 'name',
				header: 'Customer Name',
				sortable: true,
				sortType: 'string',
				render: (row: any) => `<strong>${escapeHtml(row.name)}</strong>`
			},
			{
				key: 'assets',
				header: 'Assets Count',
				sortable: true,
				sortType: 'number',
				render: (row: any) => `<s-badge tone="info">${row.assets} Assets</s-badge>`
			},
			{
				key: 'sites',
				header: 'Sites Count',
				sortable: true,
				sortType: 'number',
				render: (row: any) => `<s-badge>${row.sites} Sites</s-badge>`
			},
			{
				key: 'actions',
				header: 'Actions',
				render: (row: any) => `
					<s-button size="slim" data-action="explore-customer" data-id="${row.id}">
						Explore Locations
					</s-button>
				`
			}
		];

		const tableItems = customers
			.map((c: any) => {
				const assetsCount = (assetsListSignal.value || []).filter((a: any) => a.customerId === c.id).length;
				const sitesCount = (sitesSignal.value || []).filter((s: any) => s.customerId === c.id).length;
				return {
					id: c.id,
					name: c.name,
					assets: assetsCount,
					sites: sitesCount
				};
			})
			.filter((item: any) => item.assets > 0);
		custTable.items = tableItems;

		custTable.addEventListener('row-action', (e: CustomEvent) => {
			const { action, id } = e.detail;
			if (action === 'explore-customer') {
				window.location.hash = `#assets?level=departments&customerId=${id}`;
			}
		});
	}

	// "All Assets" card click (Level 2 -> Level 3, no dept filter)
	const allAssetsCard = document.getElementById('explorer-all-customer-assets');
	if (allAssetsCard) {
		allAssetsCard.style.cursor = 'pointer';
		allAssetsCard.addEventListener('click', () => {
			const custId = selectedExplorerCustomerIdSignal.value;
			window.location.hash = `#assets?level=assets&customerId=${custId}`;
		});
	}

	// Department card clicks (Level 2 -> Level 3, with dept filter)
	(departmentsForExplorerSignal.value || []).forEach(d => {
		const card = document.getElementById(`explorer-dept-${d.id}`);
		if (card) {
			card.style.cursor = 'pointer';
			card.addEventListener('click', () => {
				const custId = selectedExplorerCustomerIdSignal.value;
				window.location.hash = `#assets?level=assets&customerId=${custId}&deptId=${d.id}`;
			});
		}
	});

	// Breadcrumb: "All Customers" -> reset to Level 1
	const bcAllCustomers = document.getElementById('breadcrumb-all-customers');
	if (bcAllCustomers) {
		bcAllCustomers.addEventListener('click', (e) => {
			e.preventDefault();
			window.location.hash = '#assets?level=customers';
		});
	}

	// Breadcrumb: "Customer Name" -> go back to Level 2
	const bcCustomer = document.getElementById('breadcrumb-customer');
	if (bcCustomer) {
		bcCustomer.addEventListener('click', (e) => {
			e.preventDefault();
			const custId = selectedExplorerCustomerIdSignal.value;
			window.location.hash = `#assets?level=departments&customerId=${custId}`;
		});
	}

// Internal helper functions for dynamic updates without total redrawing
function updateRegSitesDropdown() {
	const sites = sitesSignal.value || [];
	const selectedRegCustId = registrationSelectedCustomerIdSignal.value;
	const filteredSites = sites.filter(s => s.customerId === selectedRegCustId);

	const siteSelect = document.getElementById('reg-site-select') as HTMLSelectElement;
	if (siteSelect) {
		siteSelect.innerHTML = filteredSites.map(s => `<option value="${s.id}">${s.name}</option>`).join('');
		updateRegGpsDisplay();
	}
}

function updateRegGpsDisplay() {
	const sites = sitesSignal.value || [];
	const siteSelect = document.getElementById('reg-site-select') as HTMLSelectElement;
	const gpsText = document.getElementById('reg-site-gps');

	if (siteSelect && gpsText) {
		const sID = siteSelect.value;
		const siteObj = sites.find(s => s.id === sID);
		if (siteObj && siteObj.latitude && siteObj.longitude) {
			gpsText.textContent = `Site GPS: ${siteObj.latitude.toFixed(4)}, ${siteObj.longitude.toFixed(4)}`;
		} else {
			gpsText.textContent = 'No coordinates available';
		}
	}
}

export function updateMapData() {
	const mapEl = document.getElementById('fleet-map') as any;
	if (!mapEl) return;

	const level = explorerLevelSignal.value;
	let mapAssets = assetsListSignal.value || [];

	const mpsFilter = assetsMpsFilterSignal.value;
	if (mpsFilter === 'mps') {
		mapAssets = mapAssets.filter(a => a.isMps === true);
	} else if (mpsFilter === 'non-mps') {
		mapAssets = mapAssets.filter(a => a.isMps === false);
	}

	if (level === 'departments' || level === 'assets') {
		const custId = selectedExplorerCustomerIdSignal.value;
		if (custId) {
			mapAssets = mapAssets.filter(a => a.customerId === custId);
		}
	}

	if (level === 'assets' && selectedExplorerDepartmentIdSignal.value) {
		const deptId = selectedExplorerDepartmentIdSignal.value;
		const dept = (departmentsForExplorerSignal.value || []).find((d: any) => d.id === deptId);
		if (dept && dept.siteIds) {
			mapAssets = mapAssets.filter(a => dept.siteIds.includes(a.siteId));
		}
	}

	const combinedCustomersMap = new Map<string, any>();
	(allCustomersSignal.value || []).forEach((c: any) => combinedCustomersMap.set(c.id, c));
	(explorerCustomersSignal.value || []).forEach((c: any) => combinedCustomersMap.set(c.id, c));
	(customersSignal.value || []).forEach((c: any) => combinedCustomersMap.set(c.id, c));

	mapEl.sites = sitesSignal.value || [];
	mapEl.customers = Array.from(combinedCustomersMap.values());
	mapEl.deviceModels = deviceModelsSignal.value || [];
	mapEl.assets = mapAssets;
}

function triggerAppReRender() {
	const breadcrumbSlot = document.getElementById('explorer-breadcrumb-slot');
	if (breadcrumbSlot) {
		breadcrumbSlot.innerHTML = getExplorerBreadcrumbHtml();
	}

	const mainContentSlot = document.getElementById('explorer-main-content');
	if (mainContentSlot) {
		mainContentSlot.innerHTML = getExplorerMainSectionHtml();
	}

	setupExplorerMainSectionListeners();
	updateMapData();
}

export function getExplorerBreadcrumbHtml(): string {
	const level = explorerLevelSignal.value;
	const selectedCustomerId = selectedExplorerCustomerIdSignal.value;
	const selectedDeptId = selectedExplorerDepartmentIdSignal.value;

	if (level === 'customers') {
		return '';
	} else if (level === 'departments') {
		const customerName = getCustomerName(selectedCustomerId);
		return `
			<div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem;">
				<a href="#assets?level=customers" id="breadcrumb-all-customers" style="color:var(--p-color-text-link); text-decoration:none; font-size:0.875rem; font-weight:500;">All Customers</a>
				<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
				<s-text style="font-weight:600; font-size:0.875rem;">${escapeHtml(customerName)}</s-text>
			</div>
		`;
	} else if (level === 'assets') {
		const customerName = getCustomerName(selectedCustomerId);
		let departmentName = '';
		if (selectedDeptId) {
			const activeDeptObj = (departmentsForExplorerSignal.value || []).find((d: any) => d.id === selectedDeptId);
			if (activeDeptObj) departmentName = activeDeptObj.name;
		}
		return `
			<div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem;">
				<a href="#assets?level=customers" id="breadcrumb-all-customers" style="color:var(--p-color-text-link); text-decoration:none; font-size:0.875rem; font-weight:500;">All Customers</a>
				<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
				<a href="#assets?level=departments&customerId=${encodeURIComponent(selectedCustomerId || '')}" id="breadcrumb-customer" style="color:var(--p-color-text-link); text-decoration:none; font-size:0.875rem; font-weight:500;">${escapeHtml(customerName)}</a>
				<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
				<s-text style="font-weight:600; font-size:0.875rem;">${escapeHtml(departmentName || 'All Assets')}</s-text>
			</div>
		`;
	}
	return '';
}

export function getExplorerMainSectionHtml(): string {
	const mpsFilter = assetsMpsFilterSignal.value;
	const fleetAssets = assetsListSignal.value || [];
	let assets = fleetAssets;
	if (mpsFilter === 'mps') {
		assets = assets.filter(a => a.isMps === true);
	} else if (mpsFilter === 'non-mps') {
		assets = assets.filter(a => a.isMps === false);
	}
	const sites = sitesSignal.value || [];
	const models = deviceModelsSignal.value || [];

	const modelMap = new Map(models.map(m => [m.id, `${m.vendor} ${m.modelName}`]));
	const siteMap = new Map(sites.map(s => [s.id, s]));

	const role = activeRoleSignal.value;
	const isManagerPlus = role === 'superadmin' || role === 'admin' || role === 'manager';

	const level = explorerLevelSignal.value;
	const selectedCustomerId = selectedExplorerCustomerIdSignal.value;
	const selectedDeptId = selectedExplorerDepartmentIdSignal.value;

	if (level === 'customers') {
		const kpiRowHtml = isManagerPlus ? `
			<s-section>
				<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Total Managed Assets</s-text>
							<s-heading element="h2">${assets.length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Active Machines</s-text>
							<s-heading element="h2">${assets.filter(a => a.status === 'active').length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Total Active Sites</s-text>
							<s-heading element="h2">${sites.length}</s-heading>
						</s-stack>
					</s-box>
				</s-grid>
			</s-section>
		` : '';

		return `
			${kpiRowHtml}
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<opm-data-table id="explorer-customers-table" searchable="true" paginated="true" page-size="10"></opm-data-table>
				</s-box>
			</s-section>
		`;

	} else if (level === 'departments') {
		const customerAssets = assets.filter(a => a.customerId === selectedCustomerId);
		const depts = departmentsForExplorerSignal.value || [];

		const kpiRowHtml = isManagerPlus ? `
			<s-section>
				<s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Total Customer Assets</s-text>
							<s-heading element="h2">${customerAssets.length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Active Machines</s-text>
							<s-heading element="h2">${customerAssets.filter(a => a.status === 'active').length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Errors / Alerts</s-text>
							<s-heading element="h2">${customerAssets.filter(a => a.status === 'error').length}</s-heading>
						</s-stack>
					</s-box>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text tone="subdued" style="text-transform:uppercase; font-size:0.75rem; font-weight:600;">Departments</s-text>
							<s-heading element="h2">${depts.length}</s-heading>
						</s-stack>
					</s-box>
				</s-grid>
			</s-section>
		` : '';

		const deptCardsHtml = depts.map(d => {
			const deptAssets = customerAssets.filter(a => d.siteIds && d.siteIds.includes(a.siteId));
			const deptAssetCount = deptAssets.length;
			const assignedSiteBadges = (d.siteIds || []).map((sID: string) => {
				const sObj = sites.find(s => s.id === sID);
				return sObj ? `<s-badge>${escapeHtml(sObj.name)}</s-badge>` : '';
			}).join(' ');

			return `
				<s-box padding="base" background="surface" border="base" borderRadius="base" 
				       id="explorer-dept-${d.id}" style="cursor:pointer;">
					<s-stack gap="small">
						<s-heading element="h3">${escapeHtml(d.name)}</s-heading>
						<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">
							${assignedSiteBadges}
						</div>
						<s-badge tone="info">${deptAssetCount} Assets</s-badge>
					</s-stack>
				</s-box>
			`;
		}).join('');

		return `
			${kpiRowHtml}
			<s-section>
				<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
					<!-- "All Assets" quick card -->
					<s-box padding="base" background="surface" border="base" borderRadius="base" 
					       id="explorer-all-customer-assets" style="cursor:pointer;">
						<s-stack gap="small">
							<s-heading element="h3">All Assets</s-heading>
							<s-text tone="subdued">View entire customer fleet</s-text>
							<s-badge tone="info">${customerAssets.length} machines</s-badge>
						</s-stack>
					</s-box>
					${deptCardsHtml}
				</s-grid>
			</s-section>
		`;

	} else if (level === 'assets') {
		const customerAssets = assets.filter(a => a.customerId === selectedCustomerId);
		let filteredAssets = customerAssets;

		if (selectedDeptId) {
			const activeDeptObj = (departmentsForExplorerSignal.value || []).find((d: any) => d.id === selectedDeptId);
			if (activeDeptObj && activeDeptObj.siteIds) {
				filteredAssets = customerAssets.filter(a => activeDeptObj.siteIds.includes(a.siteId));
			}
		}

		const statusFilter = activeFiltersSignal.value.status;
		const siteFilter = activeFiltersSignal.value.siteId;

		if (siteFilter && siteFilter !== 'all') {
			filteredAssets = filteredAssets.filter(a => a.siteId === siteFilter);
		}
		if (statusFilter && statusFilter !== 'all') {
			filteredAssets = filteredAssets.filter(a => a.status === statusFilter);
		}

		const tableRowsHtml = filteredAssets.map(a => {
			const modelName = modelMap.get(a.modelId) || 'Unknown Model';
			const siteObj = siteMap.get(a.siteId);
			const siteName = siteObj ? siteObj.name : 'Unknown Site';
			const gpsStr = siteObj && siteObj.latitude && siteObj.longitude
				? `(${siteObj.latitude.toFixed(4)}, ${siteObj.longitude.toFixed(4)})`
				: '';

			let badgeTone = 'info';
			if (a.status === 'active') badgeTone = 'success';
			if (a.status === 'error') badgeTone = 'critical';

			const isRetired = a.status === 'retired';

			return `
				<s-table-row>
					<s-table-cell><s-link href="#machine-history?id=${encodeURIComponent(a.id)}&customerId=${encodeURIComponent(selectedCustomerId || '')}${selectedDeptId ? `&deptId=${encodeURIComponent(selectedDeptId)}` : ''}"><strong>${escapeHtml(a.serialNumber)}</strong></s-link></s-table-cell>
					<s-table-cell>${escapeHtml(modelName)}</s-table-cell>
					<s-table-cell>
						<s-stack gap="none">
							<span>${escapeHtml(siteName)}</span>
							<span style="font-size: 0.8rem; color: var(--p-color-text-subdued);">${gpsStr}</span>
						</s-stack>
					</s-table-cell>
					<s-table-cell><s-badge tone="${badgeTone}">${escapeHtml(a.status)}</s-badge></s-table-cell>
					<s-table-cell>${a.isMps === false ? '<s-badge tone="attention">SERVICE ONLY</s-badge>' : '<s-badge tone="info">MPS</s-badge>'}</s-table-cell>
					<s-table-cell>${escapeHtml(a.ownershipType)}</s-table-cell>
					<s-table-cell>${a.anomalySensitivityMultiplier || 3.0}x</s-table-cell>
					<s-table-cell>
							<s-button href="#machine-history?id=${encodeURIComponent(a.id)}&customerId=${encodeURIComponent(selectedCustomerId || '')}${selectedDeptId ? `&deptId=${encodeURIComponent(selectedDeptId)}` : ''}" variant="primary" size="slim">View history</s-button>
							${isManagerPlus ? `<s-button id="btn-manual-reading-${a.id}" variant="secondary" size="slim">Add Reading</s-button><s-button id="btn-view-readings-${a.id}" size="slim">Readings</s-button><s-button id="btn-view-audit-${a.id}" size="slim">View Audit</s-button><s-button id="btn-edit-asset-${a.id}" size="slim">Edit</s-button>${!isRetired ? `<s-button id="btn-retire-asset-${a.id}" tone="critical" size="slim">Retire</s-button>` : ''}` : ''}
					</s-table-cell>
				</s-table-row>
			`;
		}).join('');

		return `
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${filteredAssets.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No assets matched the active criteria in this section.</s-text>
						</div>
					` : `
						<s-table>
							<s-table-header-row>
								<s-table-header>Serial Number</s-table-header>
								<s-table-header>Model</s-table-header>
								<s-table-header>Location</s-table-header>
								<s-table-header>Status</s-table-header>
								<s-table-header>MPS</s-table-header>
								<s-table-header>Ownership</s-table-header>
								<s-table-header>Sensitivity</s-table-header>
								<s-table-header>Actions</s-table-header>
							</s-table-header-row>
							<s-table-body>
								${tableRowsHtml}
							</s-table-body>
						</s-table>
					`}
				</s-box>
			</s-section>
		`;
	}

	return '';
}

export function setupExplorerMainSectionListeners() {
	// Re-bind listeners on dynamic table rows and department cards inside explorer-main-content
	const custTable = document.getElementById('explorer-customers-table') as any;
	if (custTable) {
		const customers = allCustomersSignal.value || [];
		custTable.columns = [
			{
				key: 'name',
				header: 'Customer Name',
				sortable: true,
				sortType: 'string',
				render: (row: any) => `<strong>${escapeHtml(row.name)}</strong>`
			},
			{
				key: 'assets',
				header: 'Assets Count',
				sortable: true,
				sortType: 'number',
				render: (row: any) => `<s-badge tone="info">${row.assets} Assets</s-badge>`
			},
			{
				key: 'sites',
				header: 'Sites Count',
				sortable: true,
				sortType: 'number',
				render: (row: any) => `<s-badge>${row.sites} Sites</s-badge>`
			},
			{
				key: 'actions',
				header: 'Actions',
				render: (row: any) => `
					<s-button size="slim" data-action="explore-customer" data-id="${row.id}">
						Explore Locations
					</s-button>
				`
			}
		];

		const tableItems = customers
			.map((c: any) => {
				const assetsCount = (assetsListSignal.value || []).filter((a: any) => a.customerId === c.id).length;
				const sitesCount = (sitesSignal.value || []).filter((s: any) => s.customerId === c.id).length;
				return {
					id: c.id,
					name: c.name,
					assets: assetsCount,
					sites: sitesCount
				};
			})
			.filter((item: any) => item.assets > 0);
		custTable.items = tableItems;

		custTable.addEventListener('row-action', (e: CustomEvent) => {
			const { action, id } = e.detail;
			if (action === 'explore-customer') {
				window.location.hash = `#assets?level=departments&customerId=${id}`;
			}
		});
	}

	const allAssetsCard = document.getElementById('explorer-all-customer-assets');
	if (allAssetsCard) {
		allAssetsCard.style.cursor = 'pointer';
		allAssetsCard.addEventListener('click', () => {
			const custId = selectedExplorerCustomerIdSignal.value;
			window.location.hash = `#assets?level=assets&customerId=${custId}`;
		});
	}

	(departmentsForExplorerSignal.value || []).forEach(d => {
		const card = document.getElementById(`explorer-dept-${d.id}`);
		if (card) {
			card.style.cursor = 'pointer';
			card.addEventListener('click', () => {
				const custId = selectedExplorerCustomerIdSignal.value;
				window.location.hash = `#assets?level=assets&customerId=${custId}&deptId=${d.id}`;
			});
		}
	});

	// Re-bind asset table action buttons (Retire, Reading, Audit, Edit)
	const assets = assetsListSignal.value || [];
	assets.forEach(a => {
		const retireBtn = document.getElementById(`btn-retire-asset-${a.id}`);
		if (retireBtn) {
			retireBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const modal = document.getElementById('retire-confirm-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const readingBtn = document.getElementById(`btn-manual-reading-${a.id}`);
		if (readingBtn) {
			readingBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const banner = document.getElementById('reading-error-banner');
				if (banner) banner.style.display = 'none';

				const modal = document.getElementById('manual-reading-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const viewReadingsBtn = document.getElementById(`btn-view-readings-${a.id}`);
		if (viewReadingsBtn) {
			viewReadingsBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const serialEl = document.getElementById('readings-asset-serial');
				if (serialEl) serialEl.textContent = a.serialNumber;

				refreshAssetReadings(a.id);

				const modal = document.getElementById('view-readings-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const viewAuditBtn = document.getElementById(`btn-view-audit-${a.id}`);
		if (viewAuditBtn) {
			viewAuditBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;
				const serialEl = document.getElementById('audit-asset-serial');
				if (serialEl) serialEl.textContent = a.serialNumber;

				const timelineEl = document.getElementById('audit-log-timeline');
				if (timelineEl) {
					let timelineHtml = `
						<div style="position: relative; padding-bottom: 0.5rem;">
							<div style="position: absolute; left: -1.35rem; top: 0.25rem; width: 8px; height: 8px; border-radius: 50%; background: var(--p-color-bg-interactive-selected);"></div>
							<s-text><strong>Asset Registered</strong></s-text>
							<s-text tone="subdued" style="font-size: 0.8rem; display: block; margin-top: 0.25rem;">Serial number ${escapeHtml(a.serialNumber)} registered under ownership type ${escapeHtml(a.ownershipType)}.</s-text>
						</div>
					`;
					if (a.status === 'retired') {
						timelineHtml += `
							<div style="position: relative; padding-bottom: 0.5rem;">
								<div style="position: absolute; left: -1.35rem; top: 0.25rem; width: 8px; height: 8px; border-radius: 50%; background: var(--p-color-text-critical);"></div>
								<s-text><strong>Asset Retired</strong></s-text>
								<s-text tone="subdued" style="font-size: 0.8rem; display: block; margin-top: 0.25rem;">Status changed to retired. Active polling disabled.</s-text>
							</div>
						`;
					}
					timelineEl.innerHTML = timelineHtml;
				}

				const modal = document.getElementById('view-audit-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const editBtn = document.getElementById(`btn-edit-asset-${a.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedAssetIdSignal.value = a.id;

				const serialInput = document.getElementById('edit-serial-number') as any;
				if (serialInput) serialInput.value = a.serialNumber;

				const intervalInput = document.getElementById('edit-interval-seconds') as any;
				if (intervalInput) intervalInput.value = (a.intervalSeconds || 3600).toString();

				const sensitivityInput = document.getElementById('edit-anomaly-multiplier') as any;
				if (sensitivityInput) sensitivityInput.value = (a.anomalySensitivityMultiplier || 3.0).toString();

				const banner = document.getElementById('edit-success-banner');
				if (banner) banner.style.display = 'none';

				const modal = document.getElementById('edit-asset-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});
}
